import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaSpinner, FaTimesCircle, FaArrowRight } from 'react-icons/fa';
import { Button } from '../components/ui/button';
import api from '../utils/api';

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('Finalisation de votre paiement...');
  const [orderId, setOrderId] = useState(null);

  useEffect(() => {
    const capturePayment = async () => {
      const provider = searchParams.get('provider');
      const token = searchParams.get('token'); // PayPal token (order ID)
      const paypalOrderId = searchParams.get('PayerID') ? token : null; // Si PayerID présent, c'est PayPal
      
      // Récupérer l'orderId depuis localStorage si nécessaire
      const storedOrderId = localStorage.getItem('pending_paypal_order');
      const orderIdToCapture = paypalOrderId || storedOrderId;
      
      if (provider === 'paypal' && orderIdToCapture) {
        try {
          setMessage('Confirmation du paiement PayPal...');
          
          const response = await api.post(`/payments/paypal/capture/${orderIdToCapture}`);
          
          if (response.data.status === 'success') {
            setStatus('success');
            setMessage(response.data.message || 'Paiement confirmé !');
            setOrderId(orderIdToCapture);
            localStorage.removeItem('pending_paypal_order');
          } else {
            throw new Error('Capture failed');
          }
        } catch (error) {
          console.error('Erreur capture PayPal:', error);
          setStatus('error');
          setMessage(error.response?.data?.detail || 'Une erreur est survenue lors de la confirmation du paiement.');
        }
      } else if (provider === 'stripe') {
        // Stripe gère la confirmation via webhook
        setStatus('success');
        setMessage('Votre paiement Stripe a été confirmé !');
      } else {
        // Pas de provider ou ordre
        setStatus('error');
        setMessage('Informations de paiement manquantes.');
      }
    };

    capturePayment();
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1B4965] to-[#0D2B3E] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center"
      >
        {status === 'processing' && (
          <>
            <FaSpinner className="animate-spin text-6xl text-[#1B4965] mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-[#1B4965] mb-4">
              Traitement en cours
            </h1>
            <p className="text-gray-600">{message}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 10 }}
            >
              <FaCheckCircle className="text-6xl text-green-500 mx-auto mb-6" />
            </motion.div>
            <h1 className="text-2xl font-bold text-[#1B4965] mb-4">
              Paiement réussi !
            </h1>
            <p className="text-gray-600 mb-6">{message}</p>
            
            {orderId && (
              <p className="text-sm text-gray-500 mb-6">
                Référence : {orderId}
              </p>
            )}
            
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/admin')}
                className="w-full bg-[#1B4965] hover:bg-[#163a52]"
              >
                <FaArrowRight className="mr-2" />
                Accéder à mon espace
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/director/subscription')}
                className="w-full"
              >
                Voir mon abonnement
              </Button>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <FaTimesCircle className="text-6xl text-red-500 mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-red-600 mb-4">
              Erreur de paiement
            </h1>
            <p className="text-gray-600 mb-6">{message}</p>
            
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/director/subscription')}
                className="w-full bg-[#1B4965] hover:bg-[#163a52]"
              >
                Réessayer
              </Button>
              <Button
                variant="outline"
                onClick={() => window.open('mailto:support@dailyglobe.school', '_blank')}
                className="w-full"
              >
                Contacter le support
              </Button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default PaymentSuccessPage;
